import WboPersistenceProvider
    from '@/components/common/ed-board/ed-board-tools/wbo-client/interfaces/wbo-persistence-provider.interface';
import WboClientOptions, {
    WboUserContext
} from '@/components/common/ed-board/ed-board-tools/wbo-client/interfaces/wbo-client-options.interface';
import WboDrawingAdapter
    from '@/components/common/ed-board/ed-board-tools/wbo-client/interfaces/wbo-drawing-adapter.interface';
import GenericQueue, {
    GenericQueueProcessor
} from '@/components/common/ed-board/ed-board-tools/wbo-client/util/generic-queue';
import {
    WboAnnotationData,
    WboAnnotationDocumentMetadata,
    WboDelta, WboServerACKResponse,
    WboFetchResponse, WboMsg, WboOpType,
    WboServerResponse, WboVersionedAnnotationData, WboBatchedBroadcastMsg
} from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-annotation.model';
import WboConnectionStatusObserver
    from '@/components/common/ed-board/ed-board-tools/wbo-client/interfaces/wbo-connection-status-observer.interface';
import {
    getVersionGaps,
    nowInMicroseconds,
    parseLastVersion
} from '@/components/common/ed-board/ed-board-tools/wbo-client/util/wbo-utils';
import WboCommandController from '@/components/common/ed-board/ed-board-tools/wbo-client/wbo-command-controller';
import WboDocument from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-document';
import WboPendingChangesQueue
    from '@/components/common/ed-board/ed-board-tools/wbo-client/model/wbo-pending-changes-queue';
import WboTransportProvider
    from '@/components/common/ed-board/ed-board-tools/wbo-client/interfaces/wbo-transport-provider.interface';
import {
    ConflictResolutionMethod
} from '@/components/common/ed-board/ed-board-tools/wbo-client/values/wbo-client.values';
import _ from 'lodash';

/**
 * The WBO Client is the main class that interacts with the WBO backend. It is responsible for managing the local
 * document, the pending changes, the incoming ops, and the transport of messages to and from the server.
 */
export default class WboClient implements WboConnectionStatusObserver {
    private readonly MAX_EMIT_MESSAGE_SIZE = 1024 * 1024 * 10;
    private static textEncoder = new TextEncoder();
    private persistenceProvider: WboPersistenceProvider;
    private commandController: WboCommandController;
    private drawingAdapter: WboDrawingAdapter;
    private document: WboDocument;
    private readonly userContext: WboUserContext;
    private readonly boardName: string;
    private isConflicted: boolean = false;
    private isInitialized: boolean = false;
    private isSubscribed: boolean = false;
    private isInSync: boolean = false;
    private isSyncing: boolean = false;

    /**
     * Queue of incoming operations (from the server) yet to be applied to the document and drawn in the canvas.
     */
    private readonly incomingOps: GenericQueue<WboDelta>;

    /**
     * Queue of persistent, raw or composed deltas ready to be sent. Its elements are removed from the queue only when
     * the server acknowledges the receipt.
     */
    private pendingChanges: WboPendingChangesQueue;

    /**
     * The transport provider that will be used to send and receive messages to and from the server.
     */
    private readonly transportProvider: WboTransportProvider;

    /**
     * A special object that processes the pending changes queue.
     */
    private pendingChangesQueueProcessor: GenericQueueProcessor<WboDelta>;

    /**
     * A special object that processes the incoming ops queue.
     * @private
     */
    private incomingOpsQueueProcessor: GenericQueueProcessor<WboDelta>;

    /**
     * A callback function that is called when the document is loaded.
     */
    private readonly onLoadCallback: (document: WboDocument, pendingChanges: WboPendingChangesQueue) => void;

    /**
     * A callback function that is called when a conflict is detected.
     */
    private readonly onConflictCallback: (localDocument: WboDocument, conflictingChange: WboVersionedAnnotationData, instance: WboClient) => void;

    constructor(private options: WboClientOptions) {
        this.log('WboClient.constructor - Initializing WboClient with options', options);
        this.userContext = options.userContext;
        this.boardName = options.boardName;
        this.incomingOps = new GenericQueue<WboDelta>();
        this.persistenceProvider = options.persistenceProvider;
        this.drawingAdapter = options.drawingAdapter;
        this.transportProvider = options.transportProvider;
        this.onLoadCallback = options.onLoad || (() => {});
        this.onConflictCallback = options.onConflict || (() => {});
        this.transportProvider.addConnectionStatusObserver(this);
        this.commandController = new WboCommandController(this.transportProvider, this.incomingOps, this.boardName);
    }

    /**
     * Initialize the client. This method must be called before any other method.
     */
    async initBoard(initialMetadata: WboAnnotationDocumentMetadata) {
        this.warn('WboClient.initBoard - Initializing the board with metadata', initialMetadata);
        await this.initializeIfNecessary();

        this.drawLocalAnnotationDocument();
        this.drawLocalPendingAnnotations();
        this.listenToDrawerActions();
        this.onLoadCallback(this.document, this.pendingChanges);
        this.subscribe(initialMetadata);
    }

    /**
     * This method is supposed to be called when the client is successfully subscribed to the WBO backend.
     */
    async onSubscribe(): Promise<void> {
        this.log('WboClient.onSubscribe - Subscribed to the board. Reinitializing the client and syncing the board.');
        await this.initializeIfNecessary();
        this.listenToIncomingRemoteActions();
        try {
            await this.syncBoard();
            this.onLoadCallback(this.document, this.pendingChanges);
            this.isSubscribed = true;
        } catch (e) {
            if (e?.cause === 'conflict') {
                this.onConflict(e.conflictingChange);
            }
        }
    }

    async syncBoard(force: boolean = false): Promise<void> {
        if (!force && this.isInSync || this.isSyncing) {
            this.log('WboClient.syncBoard - Wont sync because we are already syncing or in sync');
            return;
        }
        try {
            this.isSyncing = true; // This was to prevent multiple syncs at the same time
            // Sync Step 0. Check if we have gaps in the applied versions

            await this.fillGapIfNeeded();
            // Sync Step 1. Fetch from current version

            const currentVersion = this.document.getVersion();
            const missingChanges = await this.commandController.fetch({
                requestType: 'composed',
                request: {board: this.boardName, from: currentVersion},
            });
            // Sync Step 2. Process missing changes if needed
            if (missingChanges.version > currentVersion) {
                this.log('WboClient.syncBoard - There are missing changes to process', missingChanges);
                let conflictingChange = this.detectConflict(missingChanges);
                if (conflictingChange) {
                    this.isInSync = false;
                    return Promise.reject({cause: 'conflict', conflictingChange, missingChanges});
                } else {
                    // Sync Step 2.2. No conflicts, process the fetched missing ops
                    // Draw and apply missingChanges
                    this.processFetchedOps(missingChanges);
                }

            }
            if (missingChanges.metadata) {
                this.document.setOtherMetadata(missingChanges.metadata);
            }
            // Sync Step 3. Send pending ops (which includes composing and applying to local copy)
            const annotationsToRepaint = [...this.pendingChanges.queue().elements()];
            await this.sendPendingChanges(true);
            // Repaint pending changes to guarantee they are drawn on top, since they are going to be the more recent ones
            this.bringAnnotationsToFront(this.deltaArrayToAnnotationDataArray(annotationsToRepaint));
            this.log('WboClient.syncBoard - Synchronization is done');
            this.isInSync = true;
            this.isSyncing = false;
            this.isConflicted = false;
        } catch (error) {
            this.isInSync = false;
            this.isSyncing = false;
            this.error('Could not sync the board', error);
        }
    }

    onDisconnect() {
        this.log('WboClient.onDisconnect - Disconnected');
        this.pendingChangesQueueProcessor?.stop();
        this.isSubscribed = false;
        this.isInSync = false;
        this.isSyncing = false;
    }

    isConnected(): boolean {
        return this.transportProvider.isConnected();
    }

    async resolveConflict(method: ConflictResolutionMethod) {
        if (!this.isConflicted) {
            return;
        }
        try {
            switch (method) {
                case ConflictResolutionMethod.KeepLocal:
                    await this.resolveConflictLocal();
                    break;
                case ConflictResolutionMethod.KeepRemote:
                    await this.resolveConflictRemote();
                    break;
                case ConflictResolutionMethod.Merge:
                    await this.resolveConflictMerge();
                    break;
                default:
                    throw new Error('Unsupported conflict resolution method');
            }
            this.onConflictResolved();
        } catch (e) {
            this.isSyncing = false;
            throw e;
        }
    }

    close() {
        this.drawingAdapter.dispose();
    }

    get initialized(): boolean {
        return this.isInitialized;
    }

    get syncing(): boolean {
        return this.isSyncing;
    }

    get inSync(): boolean {
        return this.isInSync;
    }

    get conflicted(): boolean {
        return this.isConflicted;
    }

    get subscribed(): boolean {
        return this.isSubscribed;
    }

    get getBoardName(): string {
        return this.boardName;
    }

    async hasPendingChanges(): Promise<boolean> {
        if (!this.pendingChanges) {
            this.pendingChanges = await this.persistenceProvider.readPendingChangesFile();
        }
        return !this.pendingChanges.queue().isEmpty();
    }




    private async initializeIfNecessary() {
        if (!this.isInitialized) {
            try {
                this.document = await this.persistenceProvider.readAnnotationsFile();
                this.pendingChanges = await this.persistenceProvider.readPendingChangesFile();
                this.pendingChanges.pack();
                this.persistenceProvider.savePendingChangesFile();
                this.pendingChanges.setConnected(this.isConnected());
                this.initializePendingChangesQueueProcessor();
                this.initializeIncomingOpsQueuProcessor();
                this.transportProvider.addConnectionStatusObserver(this.pendingChanges);
                this.isInitialized = true;
            } catch (error) {
                this.error('Could not retrieve the pending changes files', error);
            }
        }
        else {
            this.log('WboClient.initializeIfNecessary - Already initialized, skipping initialization');
        }
    }

    /**
     * Initializes the incoming ops queue processor. This processor is responsible for processing the incoming ops
     * and applying them to the local document. This processor checks for gaps in the applied versions and fills them
     * if needed.
     */
    private initializeIncomingOpsQueuProcessor() {
        // Create a queue processor for the incoming ops queue. The processing starts automatically when items
        // are added to the queue and processor is not paused.
        this.incomingOpsQueueProcessor = new GenericQueueProcessor(this.incomingOps)
            .runOnEachItem(async delta => {
                this.processIncomingOp(delta);
                await this.fillGapIfNeeded();
            })
            .pauseWhen(() => {
                return !this.isConnected() || this.isConflicted || !this.isInSync
            });
    }

    /**
     * Initializes the pending changes queue processor. This processor is responsible for sending the pending changes to
     * the server and applying them to the local document. This processor checks for gaps in the applied versions and
     * fills them if needed.
     */
    private initializePendingChangesQueueProcessor() {
        // Create a queue processor for the pending changes queue. The processing starts automatically when items
        // are added to the queue and processor is not paused.
        this.pendingChangesQueueProcessor = new GenericQueueProcessor(this.pendingChanges.queue())
            .optimize(100, (deltas) => this.packDeltasIntoBatchDeltas(deltas))
            .runOnEachItem(async delta => {
                this.pendingChanges.addInflightOp(delta);
                await this.sendAndApplyChange(delta); // With await, we guarantee that the next delta is not sent until this one is ACKed.
                await this.fillGapIfNeeded();
                this.pendingChanges.clearInflightOps();
            })
            .pauseWhen(() => {
                return !this.isConnected() || this.isConflicted || !this.isInSync
            });
    }

    private drawLocalAnnotationDocument() {
        let annotations = this.document.getData();
        if (annotations) {
            for (const dataKey in annotations) {
                if (dataKey !== 'metadata') {
                    this.drawingAdapter.draw(annotations[dataKey]);
                }
            }
        }
    }

    private drawLocalPendingAnnotations() {
        let deltas = this.pendingChanges.queue().elements();
        if (deltas) {
            for (const delta of deltas) {
                this.drawingAdapter.draw((delta.msg as WboMsg).data);
            }
        }
    }

    /**
     * Starts to listen to the drawer actions. They got written to the rawOutgoingOps.
     */
    private listenToDrawerActions() {
        // Whenever a user draws something using the drawer engine, it puts the produced data in its output queue.
        // Then we get those elements and put them inside our outgoingOps queue, which are persisted to guarantee that
        // they are never lost.
        let drawingsOutputQueue: GenericQueue<WboDelta> = this.drawingAdapter.getDrawingsOutputQueue();
        new GenericQueueProcessor(drawingsOutputQueue)
            .runOnEachItem(async (delta) => {
                const msg = delta.msg as WboMsg;
                if (msg?.data?.tool === 'Cursor') {
                    return;
                }
                else if (delta.opType === 'removeAllByUser') {
                    delta.msg = {
                        board: this.boardName,
                        clientId: this.options.clientId,
                        operationId: nowInMicroseconds(),
                        isHost: this.userContext.isHost,
                        isOwner: this.userContext.isOwner,
                        userName: this.userContext.username
                    };
                }
                msg.operationId = nowInMicroseconds();
                msg.clientId = this.options.clientId;
                this.pendingChanges.enqueue(delta);
                this.persistenceProvider.savePendingChangesFile();
            })
            .start();

        new GenericQueueProcessor((this.drawingAdapter.getTransientEventsQueue()))
            .runOnEachItem(async (msg) => {
                // Transient events are not persisted, but they are sent to the server.
                this.commandController.send({opType: 'broadcast', msg});
            })
            .start();
    }

    /**
     * Registers a listener on the protocol handler, so it starts enqueuing remote ops.
     * Also monitors that queue to process ops.
     */
    private listenToIncomingRemoteActions() {
        this.commandController.listenRemoteMessages();
    }



    /**
     * This method processes an incoming delta from the server. It does the following:
     * 1. If the incoming delta is a fetch response, it analyses if it contains conflicting changes. If that's the case,
     * an alert is emitted.
     * 2. Otherwise, it validates the incoming message. If it is a message that needs to be applied, it applies it to
     * the local document.
     * 3. If the application is successful, then it is added to the input queue of the drawingAdapter, so it can draw it.
     */
    private processIncomingOp(delta: WboDelta) {
        // If the incoming delta is not a cursor, then we process it.
        if ((delta.msg as WboMsg)?.data?.tool !== 'Cursor') {
            this.processRegularIncomingOp(delta);
        }
        else {
            this.drawingAdapter.getDrawingsInputQueue().enqueue(delta.msg as WboMsg);
        }

    }

    /**
     * This process fetched ops. Fetched ops are those that are retrieved via a "fetch" or "compose" command. They are
     * slightly different from the regular incoming ops, so a little message transformation is needed. Once they are
     * transformed, they can go through the regular incoming ops processing pipeline.
     */
    private processFetchedOps(fetchedOps: WboFetchResponse) {
        const finalVersion = fetchedOps.version;
        for (const fetchedOp of fetchedOps.deltas) {
            const v = parseLastVersion(fetchedOp.versions);
            if (v == this.document.getVersion()) {
                continue;
            }
            let deltaToProcess: WboDelta;
            if (['removeAllByUser', 'removeForUpload', 'removeAllForUpload'].includes(fetchedOp.type as WboOpType)) {
                deltaToProcess = {v, opType: fetchedOp.type, msg: fetchedOp};
            } else {
                deltaToProcess = {
                    v, opType: 'broadcast', msg: {
                        data: fetchedOp,
                        clientId: this.options.clientId,
                        userName: fetchedOp.userName,
                        board: this.boardName,
                        operationId: fetchedOp.operationId,
                    }
                };
            }
            this.processRegularIncomingOp(deltaToProcess, fetchedOp.versions);
        }
        if (finalVersion != this.document.getVersion()) {
            this.error(`Version inconsistency. Local document version is ${this.document.getVersion()} and should  be ${finalVersion}`);

        }
    }

    /**
     * Applies the operation to the local document, then dispatch it to the drawer adapter so the drawer eventually
     * updates the canvas accordingly.
     */
    private processRegularIncomingOp(delta: WboDelta, versionString?: string) {
        try {
            // Apply document to delta and transform that delta to what the drawer has to do.
            const operationToDraw = this.document.applyOperation(delta);
            if (versionString) {
                this.document.applyVersionString(versionString);
            }
            this.persistenceProvider.saveAnnotationsFile();
            this.drawingAdapter.getDrawingsInputQueue().enqueue(operationToDraw.msg as WboMsg);
        } catch (error) {
            this.error('WboClient.processRegularIncomingOp - Some error occurred', error);
        }

    }

    /**
     * A conflict is detected when there are pending changes and the requested missing changes contain at least one
     * delta from the same user issued from another client. This means that the user has done a change from another
     * device and sent to the server while this client was offline. This is a conflict because the user has done a
     * change in two different places.
     */
    private detectConflict(missingChanges: WboFetchResponse): WboVersionedAnnotationData | null {
        const areTherePendingChanges = !this.pendingChanges.queue().isEmpty();
        if (areTherePendingChanges) {
            const deltas = missingChanges.deltas;
            if (!deltas || deltas.length == 0) {
                return null;
            }
            for (const delta of deltas) {
                // If any of the missing changes is from me, then we consider it a conflict only if I did it from a
                // different client (device). If I did it from the same client, then it is not a conflict, because it could
                // have been caused by a reconnection or delayed message that did go through, but I didn't receive an ACK
                // in a timely manner.
                if (delta.userName == this.userContext.username && delta.clientId !== this.options.clientId) {
                    return delta;
                }
            }
        }
        return null;
    }

    private onConflict(conflictingChange: WboVersionedAnnotationData) {
        // Stop everything
        this.isConflicted = true;
        this.isInSync = false;
        this.onConflictCallback(this.document, conflictingChange, this);
    }

    private onConflictResolved() {
        // Resume the pending changes queue processor
        this.isConflicted = false;
        this.isSyncing = false;
        this.isInSync = true;
        if (!this.incomingOpsQueueProcessor) {
            this.listenToIncomingRemoteActions();
        }
        // Do anything else this client needs to be operative again
    }


    private async sendPendingChanges(forceRepack: boolean = false) {
        if (forceRepack) {
            const deltasToRepack: WboDelta[] = [];
            while (!this.pendingChanges.queue().isEmpty() && this.isConnected()) {
                deltasToRepack.push(this.pendingChanges.queue().dequeue());
            }
            const packedDeltas = this.packDeltasIntoBatchDeltas(deltasToRepack);
            this.pendingChanges.queue().setQueue(packedDeltas);
        }
        while (!this.pendingChanges.queue().isEmpty() && this.isConnected()) {
            const delta = this.pendingChanges.queue().dequeue();
            await this.sendAndApplyChange(delta);
        }
    }

    /**
     * Packs the queue into as few deltas as possible. Deltas of type other than broadcast and
     * broadcastMultiple are ignored.
     */
    private packDeltasIntoBatchDeltas(deltas: WboDelta[]): WboDelta[] {
        const packedDeltas = [];
        let mustStartNewBatch = true;
        let lastBatch: WboDelta = null;
        const actualMaxSize = this.MAX_EMIT_MESSAGE_SIZE * 0.75; // 75% of the max size

        let lastBatchSize = 0; // Maintain a running total of the last batch size

        const calculateSize = (delta: WboDelta): number => WboClient.textEncoder.encode(JSON.stringify(delta)).length;

        for (const delta of deltas) {
            if (delta.opType == 'broadcast') {
                const deltaSize = calculateSize(delta);

                if (mustStartNewBatch || (lastBatch && lastBatchSize + deltaSize > actualMaxSize)) {
                    lastBatch = {
                        opType: 'broadcastMultiple',
                        msg: {
                            board: this.boardName,
                            clientId: this.options.clientId,
                            operationId: (delta.msg as WboMsg).operationId,
                            userName: this.userContext.username,
                            data: [{ ...(delta.msg as WboMsg).data, operationId: (delta.msg as WboMsg).operationId }]
                        }
                    };
                    packedDeltas.push(lastBatch);
                    lastBatchSize = calculateSize(lastBatch); // Update the size of the new batch
                } else {
                    (lastBatch.msg as WboBatchedBroadcastMsg).data.push({
                        ...(delta.msg as WboMsg).data,
                        operationId: (delta.msg as WboMsg).operationId
                    });
                    lastBatchSize += deltaSize; // Incrementally update the batch size
                }
                mustStartNewBatch = false;
            } else if (delta.opType == 'broadcastMultiple') {
                packedDeltas.push(delta);
                mustStartNewBatch = true;
            }
        }
        return packedDeltas;
    }

    /**
     * Given an array of deltas, this will create a batch delta that has all those deltas. Because broadcastMultiple
     * deltas can not be nested recursively, we need to flatten them. Deltas of type other than broadcast and
     * broadcastMultiple are ignored.
     */
    private packDeltasIntoSingleBatchDelta(deltas: WboDelta[]): WboDelta {
        if (deltas?.length == 0) {
            return null;
        }
        let count = 1;
        const delta: WboDelta = {
            opType: 'broadcastMultiple',
            msg: {
                board: this.boardName,
                clientId: this.options.clientId,
                operationId: nowInMicroseconds() + count++,
                userName: this.userContext.username,
                data: []
            }
        }
        const msg = delta.msg as WboBatchedBroadcastMsg;
        for (const deltaToPack of deltas) {
            if (deltaToPack.opType == 'broadcast') {
                msg.data.push({
                    ...(deltaToPack.msg as WboMsg).data,
                    operationId: nowInMicroseconds() + count++
                });
            } else if (deltaToPack.opType == 'broadcastMultiple') {
                (deltaToPack.msg as WboBatchedBroadcastMsg).data.forEach((packedAnnotation) => {
                    msg.data.push({...packedAnnotation, operationId: nowInMicroseconds() + count++});
                });
            }
        }
        return delta;

    }

    private async sendAndApplyChange(delta: WboDelta, reenqueue = true): Promise<WboServerResponse | undefined> {
        // TODO: Fix the throttle mechanism. Messages cannot be emitted too fast to the server.
        try {
            const response: WboServerResponse | undefined = await this.sendChange(delta);
            if (response.response.result === 'ERROR') {
                this.error('WboClient.sendPendingChange - Server just responded with an ERROR', response?.response, 'for delta', delta);
                await this.handleFailedDelta(delta, response.response);
                return response;
            }
            else {
                await this.handleSuccessfulDelta(delta, response.response);
                return response;
            }

        } catch (error) {
            if (error && reenqueue) {
                this.pendingChanges.enqueueFirst(delta);
            }
            return undefined;
        }
    }

    private async handleSuccessfulDelta(delta: WboDelta, response: WboServerACKResponse) {
        if (response.result !== 'OK') {
            return;
        }
        if (delta.opType === 'broadcastMultiple' && response?.operations?.length > 0) {
            // Iterate over response?.operations
            for (const opResult of response.operations) {
                const sentMessage = delta.msg as WboBatchedBroadcastMsg;
                const singleDelta = this.extractDeltaFromBatchedBroadcastMsg(sentMessage, opResult.operationId);
                if (!singleDelta) {
                    continue;
                }
                if (opResult.result === 'OK') {
                    await this.handleSuccessfulDelta(singleDelta, opResult);
                }
                else {
                    await this.handleFailedDelta(singleDelta, opResult);
                }
            }
        }
        else {
            if (response.version) {
                delta.v = response.version;
            }
            this.document.applyOperation(delta);
            this.persistenceProvider.savePendingChangesFile();
            this.persistenceProvider.saveAnnotationsFile();
        }
    }

    private async handleFailedDelta(failedDelta: WboDelta, response: WboServerACKResponse) {
        if (response.result !== 'ERROR') {
            return;
        }
        this.error('WboClient.sendPendingChange - Server just responded with an ERROR', response, 'for delta', failedDelta);
        if (['NO_ELEMENTS_TO_DELETE', 'INVALID_MESSAGE', 'BLOCKED_MESSAGE', 'NOT_APPLIED'].includes(response.error)) {
            this.error(`WboClient.sendPendingChange - ACK is ${response.error} for delta. Won't retry.`);
            return;
        }
        else if (response.error === 'BOARD_NOT_LOADED') {
            this.pendingChanges.enqueueFirst(failedDelta);
            // Force reload
            this.subscribe(this.document.getData().metadata);
            return;
        }

        else if (response.error === 'BANNED_CLIENT') {
            this.pendingChanges.enqueueFirst(failedDelta);
            await new Promise(resolve => setTimeout(resolve, 1024));
            return response;
        }
        else {
            this.error('WboClient.handleFailedDelta - ' + response.error + ' is an unknown error, so we are clueless about how to deal with it');
        }
        return response;

    }

    /**
     * Sends
     */
    private async sendChange(delta: WboDelta): Promise<WboServerResponse | undefined> {
        if (this.isConnected()) {
            return this.commandController.sendWithAck(delta);
        }
        return Promise.resolve(undefined);
    }


    async onConnect() {
        this.log('WboClient.onConnect - Connected, attempting to subscribe to the board.');
        if (!this.document) {
            await this.initializeIfNecessary();
        }
        if (!this.isSubscribed) {
            this.subscribe(this.document.getData().metadata);
        }
    }

    onReconnect() {
        this.subscribe(this.document.getData().metadata);
    }

    /**
     * Resolves a conflict by keeping the local changes and discarding the remote changes. This is done by packing the
     * own local annotations along with the pending changes again, send them in a single batch, then apply them to the
     * local document.
     */
    private async resolveConflictLocal() {
        const deltasToSendInASingleBatch: WboDelta[] = [];
        const lastKnownVersion = this.document.getVersion();

        // We fetch the missing changes again to keep those made by other users (which, by definition, are non-conflicting)
        const missingChanges = await this.commandController.fetch({
            requestType: 'composed',
            request: {board: this.boardName, from: lastKnownVersion},
        });
        missingChanges.deltas = missingChanges.deltas.filter(delta => delta.userName !== this.userContext.username);

        const ownLocalAnnotations: WboAnnotationData[] = [];
        let data = this.document.getData();
        Object.keys(data).forEach((key) => {
            if (key !== 'metadata' && data[key].userName === this.userContext.username) {
                let annotationCopy = _.omit(data[key], ['version', 'versions']);
                ownLocalAnnotations.push(annotationCopy);
            }
        });
        ownLocalAnnotations.forEach(annotation => {
            deltasToSendInASingleBatch.push({
                opType: 'broadcast',
                msg: {
                    board: this.boardName,
                    clientId: this.options.clientId,
                    operationId: nowInMicroseconds(),
                    userName: this.userContext.username,
                    data: annotation
                }
            });
        });


        // Process incoming changes from others made while we were offline
        this.processFetchedOps(missingChanges); 
        this.pendingChanges.pack();
        const pendingDeltas = this.pendingChanges.queue().elements();
        pendingDeltas.forEach(delta => {
            deltasToSendInASingleBatch.push(delta);
        });

        const singleDelta: WboDelta = this.packDeltasIntoSingleBatchDelta(deltasToSendInASingleBatch);
        if ((singleDelta.msg as WboBatchedBroadcastMsg).data?.length == 0) {
            return;
        }

        // Attempt to send the single delta. If it fails, it should not add that delta to the pending changes queue
        // because the next time the board goes online, it should detect the conflict again and build this single delta
        // again.
        (singleDelta.msg as WboBatchedBroadcastMsg).removeForUpload = true;
        const result = await this.sendAndApplyChange(singleDelta, false);
        if (!result) {
            throw new Error('Could not resolve the conflict');
        }
        // Force the applying of the version. This will avoid a gap in the local document.
        this.document.applyVersionString(`${lastKnownVersion}-${this.document.getVersion()}`);

        // Reset queues to start fresh
        this.pendingChanges.clear();
        this.incomingOps.clear();

        // Redraw the board to avoid any inconsistencies
        this.drawingAdapter.clear();
        this.drawLocalAnnotationDocument();
        this.drawLocalPendingAnnotations();
    }


    /**
     * Resolves a conflict by keeping the remote changes and discarding the local changes. This is done by first
     * clearing the local changes, then fetching the remote changes again.
     */
    private async resolveConflictRemote() {
        let wboFetchResponse = await this.commandController.fetch({
            requestType: 'composed',
            request: {board: this.boardName, from: 0}
        });
        // This first wipes out the local changes, then fetches everything again.
        this.drawingAdapter.clear();
        this.pendingChanges.clear();
        this.document.reset();
        this.processFetchedOps(wboFetchResponse);
        this.persistenceProvider.saveAnnotationsFile();
        this.persistenceProvider.savePendingChangesFile();
    }

    /**
     * Resolves a conflict by merging both the local changes with the remote changes. This is done by first applying the
     * remote changes, then sending the local changes.
     */
    private async resolveConflictMerge() {
        // This first applies latest missing changes from the server, then sends and applies the local pending changes
        let wboFetchResponse = await this.commandController.fetch({
            requestType: 'composed',
            request: {board: this.boardName, from: this.document.getVersion()}
        });
        this.processFetchedOps(wboFetchResponse);
        const annotationsToRepaint = [...this.pendingChanges.queue().elements()];
        await this.sendPendingChanges(true);
        this.persistenceProvider.savePendingChangesFile();
        this.persistenceProvider.saveAnnotationsFile();
        this.drawingAdapter.batchDraw(this.deltaArrayToAnnotationDataArray(annotationsToRepaint));
    }

    /**
     * Checks if there are gaps in the applied versions and fills them if needed. A gap is a missing version between the
     * first and last applied versions. This is identified with the help of the appliedVersions metadata field.
     */
    private async fillGapIfNeeded() {
        let appliedVersions = this.document.getData().metadata?.appliedVersions;
        try {
            const gaps = getVersionGaps(appliedVersions);
            if (gaps.length > 0) {
                this.log('WboClient.fillGapIfNeeded - Found gaps: ' + JSON.stringify(gaps));
                await this.fillGaps(gaps);
            }
        } catch (e) {
            this.error('WboClient.fillGapIfNeeded - Could not fill the gap', e);
        }
    }

    /**
     * Fills gaps in the local document.
     * @param gaps
     * @private
     */
    private async fillGaps(gaps: {start: number, end: number}[]) {
        // Only care about the first gap. Because we will fill it to the end
        if (gaps.length > 0) {
            await this.fillGap(gaps[0].start);
        }
    }

    /**
     * Fills a specific gap in the document. This is done by fetching the missing changes from the server and applying
     * them to the local document.
     */
    private async fillGap(start: number) {
        this.log('WboClient.fillGap - Filling gap from ' + start + ' to the end.');
        const missingChanges = await this.commandController.fetch({
            requestType: 'composed',
            request: {board: this.boardName, from: start},
        });
        if (missingChanges?.deltas?.length > 0) {
            this.processFetchedOps(missingChanges);
            // After this, the gaps should be filled. If there are still gaps, then we have a problem with the server.
            // We will mark them as applied, so we don't try to fill them again.
            const remainingGaps = getVersionGaps(this.document.getData().metadata?.appliedVersions);
            if (remainingGaps.length > 0) {
                this.document.applyVersionString(`${start}-${missingChanges.version}`);
            }
        }
    }

    /**
     * Issues a subscription request to the server. This is the first step to start receiving messages from the server.
     */
    private subscribe(metadata: WboAnnotationDocumentMetadata) {
        if (!this.isSubscribed) {
            this.commandController.subscribe(this.boardName, metadata);
        }
    }

    private log(message: string, ...rest: any[]) {
        // const status = {
        //     isInSync: this.isInSync,
        //     isSyncing: this.isSyncing,
        //     isInitialized: this.isInitialized,
        //     isConflicted: this.isConflicted,
        //     isSubscribed: this.isSubscribed
        // };
        // console.log(`${this.boardName} - ${message}`, ...rest, status);
    }

    private warn(message: string, ...rest: any[]) {
        // console.warn(`${this.boardName} - ${message}`, ...rest);
    }

    private error(message: string, ...rest: any[]) {
        // console.error(`${this.boardName} - ${message}`, ...rest);
    }

    private trace(message: string, ...rest: any[]) {
        // console.trace(`${this.boardName} - ${message}`, ...rest);
    }

    private extractDeltaFromBatchedBroadcastMsg(sentMessage: WboBatchedBroadcastMsg, operationId: number): WboDelta | undefined {
        const failedData = _.find(sentMessage.data, {operationId});
        if (failedData) {
            return {
                opType: 'broadcast',
                msg: {
                    board: sentMessage.board,
                    clientId: sentMessage.clientId,
                    data: failedData,
                    operationId,
                    userName: sentMessage.userName
                }
            };
        }
        return undefined;
    }

    bringAnnotationsToFront(annotations: WboAnnotationData[]) {
        for (const annotation of annotations) {
            this.drawingAdapter.bringToFront(annotation.id);
        }
    }

    private deltaArrayToAnnotationDataArray(deltas: WboDelta[]): WboAnnotationData[] {
        const annotations: WboAnnotationData[] = [];
        for (const delta of deltas) {
            if (delta.opType === 'broadcastMultiple') {
                const sentMessage = delta.msg as WboBatchedBroadcastMsg;
                annotations.push(...sentMessage.data);
            }
            else if (delta.opType === 'broadcast') {
                annotations.push((delta.msg as WboMsg).data);
            }
        }
        return annotations;
    }
}
